import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  UserMinus,
  X,
} from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import { exitHandoverPreview, exitHandoverResult } from '../lib/types'
import { formatNumber } from '../lib/format'

export function ExitHandoverDialog({
  open,
  userId,
  userName,
  onClose,
  onCommitted,
}) {
  const qc = useQueryClient()
  const [destinationUserId, setDestinationUserId] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  // Reset picker + confirmation when the dialog closes OR when the
  // target user changes. Without this, re-opening the dialog for a
  // different teammate carried the previous destination + checkbox
  // forward — the admin could accidentally commit the new handover to
  // a destination they picked for someone else entirely, with the
  // checkbox pre-ticked. Re-arming on (open, userId) is the safer
  // contract.
  useEffect(() => {
    if (!open) {
      setDestinationUserId('')
      setConfirmed(false)
    }
  }, [open, userId])

  // Form dialog — Escape closes, backdrop click does NOT (matches the
  // shared Modal's dismissal model; see SupportPage.ComposeDialog).
  // Gated on `open` because this component stays mounted while closed.
  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const previewQuery = useQuery({
    queryKey: ['exit-handover', userId],
    queryFn: async () => {
      const res = await api.get(
        `/admin/users/${userId}/exit-handover/preview`,
      )
      return exitHandoverPreview.parse(res.data)
    },
    enabled: open && Boolean(userId),
  })

  // Candidate list now comes inline with the preview response —
  // server-side filters to teammates sharing at least one department
  // with the leaver (falling back to all active tenant users when no
  // shared dept exists). Previously a separate /users query ran here
  // with a pageSize that failed Zod validation, so the dropdown was
  // always empty.

  const commitMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/admin/users/${userId}/exit-handover`, {
        destinationUserId,
      })
      return exitHandoverResult.parse(res.data)
    },
    onSuccess: (result) => {
      toast.success(
        `Reassigned ${result.reassignedAssignments} task${result.reassignedAssignments === 1 ? '' : 's'} + offboarded the user.`,
      )
      void qc.invalidateQueries({ queryKey: ['members'] })
      onCommitted?.()
      onClose()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  if (!open || !userId) return null

  // Candidates come from the preview response, pre-filtered by
  // department on the backend. The defensive client-side filter
  // (id !== userId, status === 'active') is kept as a belt-and-
  // braces guard against stale data — server already excludes both.
  const candidates = (previewQuery.data?.candidates ?? []).filter(
    (m) => m.id !== userId && m.status === 'active',
  )
  const fallbackToAll = previewQuery.data?.fallbackToAll === true

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
      <div className="w-[640px] max-h-[90vh] flex flex-col rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl">
        <div className="px-5 py-4 border-b border-[color:var(--color-rule)] flex items-center justify-between">
          <p className="font-semibold text-[color:var(--color-ink-strong)] inline-flex items-center gap-2">
            <UserMinus size={16} />
            Exit handover{userName ? ` — ${userName}` : ''}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          <div className="rounded-md border border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)] p-3 text-xs text-[color:var(--color-ink)] inline-flex items-start gap-2">
            <AlertTriangle
              size={14}
              className="text-[color:var(--color-warning)] shrink-0 mt-0.5"
            />
            <p>
              <strong>This action moves work and offboards the user.</strong>{' '}
              Tasks they're assigned to + tasks they created will get
              re-pointed to the destination user, their space memberships
              will move, and their account will be flagged Offboarded so
              they can no longer sign in. Audit log captures the full move.
            </p>
          </div>

          {previewQuery.isLoading ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
              <Loader2 size={13} className="animate-spin" />
              Building preview…
            </p>
          ) : previewQuery.isError ? (
            <p className="text-sm text-[color:var(--color-priority-urgent)]">
              {extractApiError(previewQuery.error).message}
            </p>
          ) : previewQuery.data ? (
            <div className="grid grid-cols-2 gap-3">
              <PreviewStat
                label="Tasks assigned"
                value={previewQuery.data.summary.tasksAssignedCount}
              />
              <PreviewStat
                label="Tasks created"
                value={previewQuery.data.summary.tasksCreatedCount}
              />
              <PreviewStat
                label="Space memberships"
                value={previewQuery.data.summary.spaceMembershipsCount}
              />
            </div>
          ) : null}

          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
              Reassign everything to<span className="text-[color:var(--color-priority-urgent)] ml-0.5">*</span>
            </label>
            <select
              value={destinationUserId}
              onChange={(e) => setDestinationUserId(e.target.value)}
              disabled={previewQuery.isLoading || previewQuery.isError}
              className="w-full h-10 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="" disabled>
                {previewQuery.isLoading
                  ? 'Loading teammates…'
                  : previewQuery.isError
                    ? 'Could not load teammates'
                    : candidates.length === 0
                      ? 'No other active teammates available'
                      : 'Pick a teammate'}
              </option>
              {candidates.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.fullName} — {m.emailId}
                  {m.sharedDepartments && m.sharedDepartments.length > 0
                    ? ` · ${m.sharedDepartments.join(', ')}`
                    : ''}
                </option>
              ))}
            </select>
            {/* Three explanatory hints, mutually exclusive: */}
            {/* 1) Load error → show the error inline. */}
            {previewQuery.isError ? (
              <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                {extractApiError(previewQuery.error).message}
              </p>
            ) : null}
            {/* 2) Fallback path → the leaver had no shared-department
             *    teammates, so we widened to every active user. Tell
             *    the admin so the list isn't surprisingly broad. */}
            {!previewQuery.isLoading &&
            !previewQuery.isError &&
            fallbackToAll &&
            candidates.length > 0 ? (
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                No teammates share a department with{' '}
                {userName ?? 'this user'} — showing every active
                teammate so you can still complete the handover.
              </p>
            ) : null}
            {/* 3) Happy path → the list is department-scoped. Just
             *    tell the admin what filter is being applied. */}
            {!previewQuery.isLoading &&
            !previewQuery.isError &&
            !fallbackToAll &&
            candidates.length > 0 ? (
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                Showing teammates who share a department with{' '}
                {userName ?? 'this user'}.
              </p>
            ) : null}
            {/* 4) Empty + no error + no fallback shouldn't normally
             *    happen (fallback is supposed to widen), but cover
             *    it anyway. */}
            {!previewQuery.isLoading &&
            !previewQuery.isError &&
            candidates.length === 0 ? (
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                No other active teammates in this company. Add at
                least one before offboarding this user.
              </p>
            ) : null}
          </div>

          <label className="inline-flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm text-[color:var(--color-ink)]">
              I understand this will offboard{' '}
              <span className="font-semibold">{userName ?? 'this user'}</span>{' '}
              and reassign their work. This cannot be undone in one click.
            </span>
          </label>
        </div>

        <div className="px-5 py-3 border-t border-[color:var(--color-rule)] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded text-sm border border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => commitMutation.mutate()}
            disabled={
              commitMutation.isPending || !destinationUserId || !confirmed
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold bg-[color:var(--color-danger)] text-white hover:opacity-90 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <CheckCircle2 size={12} />
            {commitMutation.isPending
              ? 'Committing…'
              : 'Commit handover & offboard'}
          </button>
        </div>
      </div>
    </div>
  )
}

function PreviewStat({ label, value }) {
  return (
    <div className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-3">
      <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
        {label}
      </p>
      <p className="mt-1 font-display text-2xl text-[color:var(--color-ink-strong)] tabular-nums">
        {formatNumber(value)}
      </p>
    </div>
  )
}
